import React, {
  useMemo,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Accordion from 'core/components/Accordion';
import {
  MissingServiceLineInfo,
} from 'dtos';
import {
  groupServiceLinesByClaim,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/Discrepancy/utils';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

type ServiceLineReadOnlySectionProps = {
  title: string;
  items: MissingServiceLineInfo[];
};

function ServiceLineReadOnlySection({
  title,
  items,
}: ServiceLineReadOnlySectionProps) {
  const grouped = useMemo(() => groupServiceLinesByClaim(items), [items]);

  return items.length === 0 ? null : (
    <div className="rounded border border-gray-200 py-2">
      <div className="w-full text-xs font-semibold px-2.5 text-gray-800 border-b border-gray-200 pb-2 bg-[#FAEEDA22]">{title}</div>
      <div className="mt-1.5 flex flex-col gap-1.5">
        {grouped.map((group) => (
          <div key={group.key} className="rounded px-2 py-1.5 flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <ErrorOutlineOutlinedIcon className="!h-4 !w-4 !text-orange-900 shrink-0" />
                <span className="text-xs font-semibold text-gray-800">{group.patientName ?? '—'}</span>
              </div>
              {!isNil(group.dateOfService) && !isEmpty(group.dateOfService) && (
              <span className="text-xs text-gray-600">
                Visit
                {' '}
                {group.dateOfService}
              </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {group.serviceLines.map((line) => (
                <div key={line} className="text-xs text-gray-500 px-1.5 py-0.5 border border-gray-200 rounded-full bg-[#FAEEDA22]">{line}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

type Props = {
  hasServiceLineDiscrepancies: boolean;
  serviceLineEobMissingInClaim: MissingServiceLineInfo[];
  serviceLineClaimMissingInEob: MissingServiceLineInfo[];
};

export default function ServiceLineDiscrepanciesSection({
  hasServiceLineDiscrepancies,
  serviceLineEobMissingInClaim,
  serviceLineClaimMissingInEob,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const Icon = expanded ? KeyboardArrowDownIcon : KeyboardArrowRightIcon;

  return hasServiceLineDiscrepancies ? (
    <Accordion
      expanded={expanded}
      onChange={(_, nextExpanded) => { setExpanded(nextExpanded); }}
      className="!bg-transparent !shadow-none !border-0 !before:hidden !my-0"
      expandIcon={<span className="hidden" />}
      header={(
        <div className="flex items-center gap-0.5 text-[13px] font-semibold text-gray-800">
          <Icon className="!h-4 !w-4" />
          <span>Service line discrepancies </span>
          <span className="font-normal text-gray-600">(view-only)</span>
        </div>
      )}
      headerClasses={{
        root: '!min-h-0 !px-0 !h-6 !py-0',
        content: '!m-0',
        expandIconWrapper: '!hidden',
      }}
      detailsClasses={{ root: '!p-0' }}
    >
      <div className="flex flex-col gap-1.5">
        <ServiceLineReadOnlySection
          title={`EOB service lines missing in claim (${serviceLineEobMissingInClaim.length})`}
          items={serviceLineEobMissingInClaim}
        />
        <ServiceLineReadOnlySection
          title={`Claim service lines missing in EOB (${serviceLineClaimMissingInEob.length})`}
          items={serviceLineClaimMissingInEob}
        />
      </div>
    </Accordion>
  ) : null;
}
